import { useQuery } from "../data/useQuery.js";
import { useBoundQuery, FillPrompt } from "../data/DwSql.js";
import { ErrorBadge } from "./ErrorBadge.js";
import { compactLabel, formatForName, parseFormat } from "./format.js";
import { yDomain } from "./scale.js";
import type { QueryResult } from "../../shared/types.js";
import type { VizProps } from "./registry.js";

// Dependency-free SVG line chart. First column (or x=) is the x-axis; remaining
// numeric columns (or series="a,b") are smoothed lines. `right="col"` puts a
// series on a second (right) y-axis.
const PALETTE = ["#7c5cff", "#22d3ee", "#22c55e", "#f59e0b", "#ef4444", "#a78bfa"];

export function LineChartViz({ source, attrs }: VizProps) {
  const { sql, values, path, status, missing } = useBoundQuery(source.trim());
  const q = useQuery(status === "ready" ? sql : null, {
    // Explicit attr overrides the query's @maxAge pragma; absent → pragma →
    // forever (useQuery sends maxAgeDefault).
    ttl: attrs.maxAge ?? attrs.ttl,
    values,
    path,
  });
  return (
    <div className="dw-embed dw-embed-chart">
      {status === "blocked" && <FillPrompt names={missing} />}
      {status === "ready" && q.status === "loading" && (
        <div className="dw-loading">Running…</div>
      )}
      {status === "ready" && q.status === "error" && (
        <ErrorBadge message={q.error.message} />
      )}
      {status === "ready" && q.status === "ready" && (
        <LineChart result={q.data} attrs={attrs} />
      )}
    </div>
  );
}

type Pt = { x: number; y: number };

// Catmull-Rom → cubic bézier for a smooth (monotone-ish) curve through points.
function smoothPath(pts: Pt[]): string {
  if (pts.length === 0) return "";
  if (pts.length < 3) return "M " + pts.map((p) => `${p.x} ${p.y}`).join(" L ");
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`;
  }
  return d;
}

function LineChart({
  result,
  attrs,
}: {
  result: QueryResult;
  attrs: Record<string, string>;
}) {
  const colNames = result.columns.map((c) => c.name);
  const xKey = attrs.x && colNames.includes(attrs.x) ? attrs.x : colNames[0];
  const xi = colNames.indexOf(xKey);
  const rightCols = new Set(
    (attrs.right ?? "").split(",").map((s) => s.trim()).filter(Boolean),
  );

  const seriesNames = (
    attrs.series ? attrs.series.split(",").map((s) => s.trim()) : colNames
  ).filter((name) => name !== xKey && colNames.includes(name));
  if (seriesNames.length === 0) {
    return <div className="dw-empty">No numeric series to plot.</div>;
  }
  const fmtOf = parseFormat(attrs.format);

  const cats = result.rows.map((r) => String(r[xi] ?? ""));
  const series = seriesNames.map((name, s) => ({
    name,
    color: PALETTE[s % PALETTE.length],
    fmt: fmtOf(name) ?? formatForName(name),
    right: rightCols.has(name),
    vals: result.rows.map((r) => Number(r[colNames.indexOf(name)] ?? 0) || 0),
  }));

  const valsOf = (right: boolean) =>
    series.filter((s) => s.right === right).flatMap((s) => s.vals);
  // ymin=/ymax= pin the left axis; the right axis stays data-driven, since a
  // secondary series is on its own scale and a shared pin would misplace it.
  const left = yDomain(valsOf(false), attrs);
  const right = yDomain(valsOf(true), {});
  const hasRight = series.some((s) => s.right);
  // An axis carries one unit, so its ticks take the first series on it.
  const fmtAt = (onRight: boolean) =>
    series.find((s) => s.right === onRight)?.fmt ?? "number";

  const W = Math.max(640, cats.length * 60);
  const H = Number(attrs.height) || 300;
  const m = { top: 16, right: hasRight ? 48 : 16, bottom: 40, left: 56 };
  const plotW = W - m.left - m.right;
  const plotH = H - m.top - m.bottom;
  const xAt = (i: number) =>
    m.left + (cats.length === 1 ? plotW / 2 : (i / (cats.length - 1)) * plotW);
  const yAt = (v: number, onRight: boolean) => {
    const [lo, hi] = onRight ? right : left;
    return m.top + plotH - ((v - lo) / (hi - lo)) * plotH;
  };
  const ticks = [0, 0.5, 1];
  const at = ([lo, hi]: [number, number], t: number) => lo + t * (hi - lo);
  // Only worth drawing when the domain straddles it — otherwise it's the axis.
  const showZero = left[0] < 0 && left[1] > 0;

  return (
    <div className="dw-chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="none"
        style={{ height: H }}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={m.left} x2={W - m.right} y1={m.top + plotH - t * plotH}
              y2={m.top + plotH - t * plotH} stroke="var(--dw-border)"
              strokeDasharray="3 3" />
            <text x={m.left - 8} y={m.top + plotH - t * plotH + 4} textAnchor="end"
              fontSize="11" fill="var(--dw-muted)">
              {compactLabel(at(left, t), fmtAt(false))}
            </text>
            {hasRight && (
              <text x={W - m.right + 8} y={m.top + plotH - t * plotH + 4}
                textAnchor="start" fontSize="11" fill="var(--dw-muted)">
                {compactLabel(at(right, t), fmtAt(true))}
              </text>
            )}
          </g>
        ))}
        {showZero && (
          <line x1={m.left} x2={W - m.right} y1={yAt(0, false)} y2={yAt(0, false)}
            stroke="var(--dw-border)" />
        )}
        {series.map((s) => (
          <path key={s.name} fill="none" stroke={s.color} strokeWidth={2}
            d={smoothPath(s.vals.map((v, i) => ({ x: xAt(i), y: yAt(v, s.right) })))} />
        ))}
        {cats.map((cat, i) => (
          <text key={i} x={xAt(i)} y={H - m.bottom + 16} textAnchor="middle"
            fontSize="11" fill="var(--dw-muted)">
            {cat}
          </text>
        ))}
      </svg>
      <div className="dw-chart-legend">
        {series.map((s) => (
          <span key={s.name} className="dw-legend-item">
            <span className="dw-legend-swatch" style={{ background: s.color }} />
            {s.name}
            {s.right ? " (right)" : ""}
          </span>
        ))}
      </div>
    </div>
  );
}
